import { CollectionReptile } from '../components/collection/types/CollectionTypes.ts';
import { isChanceGene, GeneExpression, GeneType } from '../types/Gene.ts';
import { Morph } from '../types/Morph.ts';

type PairingGene = {
    gene_id: number;
    gene_name: string;
    gene_type: string;
    mother_expression: GeneExpression | null;
    father_expression: GeneExpression | null;
}
type GenePossibility = {
    gene_id: number;
    gene_name: string;
    gene_type: string;
    expressions: (GeneExpression | null)[];
}

type PhenotypeGene = {
    gene_name: string;
    gene_type: string;
    expression: GeneExpression | null;
}

type Combos = PhenotypeGene[];

const geneId = (gene: any): number => {
    return typeof gene?.gene_id === 'number' ? gene.gene_id : gene?.id;
}

const geneType = (gene: any): string => {
    return gene?.gene_type as string;
}

const normalizedGeneType = (raw: string): GeneType | string => {
    if (raw === 'Dom') return 'Dominant';
    if (raw === 'Rec') return 'Recessive';
    return raw;
}

export const PhenotypeCalculator = (mother: CollectionReptile, father: CollectionReptile, morphs: Morph[] = []) => {
    // this function needs to find all the genes shared by the mother and father
    // and then calculate the phenotype of the offspring
    // The only phenotypes possible are NORMAL, SUPER, HET, POSSIBLE_HET, FIFTY_PERCENT, SIXTY_SIX_PERCENT

    // if the gene is recessive and one parent is NORMAL and the doesn't have the gene, the offspring can only be HET
    // if the gene is recessive and both parents are HET, the results we need to have 2 values for the gene NORMAL and SIXTY_SIX_PERCENT
    // if the gene is recessive and one parent is HET and the other is NORMAL, the results we need to have 2 values for the gene NORMAL and HET
    // if the gene is recessive and both parents are NORMAL, the results we need to have 1 value for the gene NORMAL

    // if the gene is codominant or dom and one parent is NORMAL and the other is SUPER, the results we need to have 2 values for the gene NORMAL and SUPER
    // if the gene is codominant or dom and both parents are SUPER, the results we need to have 1 value for the gene SUPER
    // if the gene is codominant or dom and both parents are NORMAL, the results we need to have 2 values for the gene NORMAL and SUPER
    // if the gene is codominant or dom and one parent is SUPER and the other doesn't have the gene, the results we need to have 1 value for the gene NORMAL
    // if the gene is codominant or dom and one parent is NORMAL and the other doesn't have the gene, the results we need to have 1 value for the gene NORMAL

    // if the gene is line bred and both parents are NORMAL, the results we need to have 1 value for the gene NORMAL
    // if the gene is line bred and one parent is NORMAL and the other doesn't have the gene, the results we need to have 1 value for the gene NORMAL

    // if the gene is unproven, the results we need to have 1 value for the gene NORMAL




    // now we need to calculate the phenotype of the offspring based on the shared genes
    // to do this we need to create an array of objects that have the gene name, the gene type, and the expression
    // using the logic above we can determine the expression of the offspring

    const motherGenes = mother.genes.length ? mother.genes : morphs.find((morph) => morph.morph_name_id === mother.morph_name_id)?.genes || [];
    const fatherGenes = father.genes.length ? father.genes : morphs.find((morph) => morph.morph_name_id === father.morph_name_id)?.genes || [];

    const genes: PairingGene[] = [];
    fatherGenes.forEach((fatherGene) => {
        const gene = motherGenes.find((motherGene) => motherGene.gene_name === fatherGene.gene_name);
        genes.push({
            gene_id: geneId(fatherGene),
            gene_name: fatherGene.gene_name,
            gene_type: geneType(fatherGene),
            father_expression: fatherGene.expression,
            mother_expression: gene?.expression || null
        });
    });

    motherGenes.forEach((motherGene) => {
        const gene = fatherGenes.find((fatherGene) => fatherGene.gene_name === motherGene.gene_name);
        if (!gene) {
            genes.push({
                gene_id: geneId(motherGene),
                gene_name: motherGene.gene_name,
                gene_type: geneType(motherGene),
                mother_expression: motherGene.expression,
                father_expression: null
            });
        }
    });

    const results: {
        [key: string]: GenePossibility
    } = {};
    genes.forEach((gene) => {
        results[gene.gene_name] = {
            gene_id: gene.gene_id,
            gene_name: gene.gene_name,
            gene_type: gene.gene_type,
            expressions: []
        };
    });

    genes.forEach((gene) => {
        const geneTypeNormalized = normalizedGeneType(gene.gene_type);
        if (geneTypeNormalized === 'Dominant' || geneTypeNormalized === 'Codom') {
            if (gene.mother_expression === 'NORMAL' && gene.father_expression === 'NORMAL') {
                results[gene.gene_name].expressions.push('NORMAL');
                results[gene.gene_name].expressions.push('SUPER');
            } else if (gene.mother_expression === 'SUPER' && gene.father_expression === 'SUPER') {
                results[gene.gene_name].expressions.push('SUPER');
            } else if (
                (gene.mother_expression === 'NORMAL' && gene.father_expression === null) ||
                (gene.father_expression === 'NORMAL' && gene.mother_expression === null)) {
                results[gene.gene_name].expressions.push('NORMAL');
                results[gene.gene_name].expressions.push(null);
            } else if (
                (gene.mother_expression === 'SUPER' && gene.father_expression === null) ||
                (gene.father_expression === 'SUPER' && gene.mother_expression === null)
            ) {
                results[gene.gene_name].expressions.push('NORMAL');
            } else if (
                (gene.mother_expression === 'SUPER' && gene.father_expression === 'NORMAL') ||
                (gene.father_expression === 'SUPER' && gene.mother_expression === 'NORMAL')
            ) {
                results[gene.gene_name].expressions.push('SUPER');
                results[gene.gene_name].expressions.push('NORMAL');
            } else {
                console.log('ERROR', gene)
            }
        } else if (geneTypeNormalized === 'Recessive') {
            if (gene.mother_expression === 'NORMAL' && gene.father_expression === 'NORMAL') {
                // NORMAL X NORMAL = NORMAL

                results[gene.gene_name].expressions.push('NORMAL');

            } else if ((gene.father_expression === 'NORMAL' && gene.mother_expression === 'HET') ||
                (gene.father_expression === 'HET' && gene.mother_expression === 'NORMAL')) {
                // NORMAL X HET = HET | NORMAL

                results[gene.gene_name].expressions.push('HET');
                results[gene.gene_name].expressions.push('NORMAL');
            }
            else if (gene.mother_expression === 'HET' && gene.father_expression === 'HET') {
                // HET X HET = NORMAL | SIXTY_SIX_PERCENT
                results[gene.gene_name].expressions.push('SIXTY_SIX_PERCENT');
                results[gene.gene_name].expressions.push('NORMAL');

            } else if (
                (gene.father_expression === 'HET' && gene.mother_expression === null) ||
                (gene.mother_expression === 'HET' && gene.father_expression === null)
            ) {
                // HET X NULL = FIFTY_PERCENT
                results[gene.gene_name].expressions.push('FIFTY_PERCENT');
            } else if (
                (gene.father_expression === 'NORMAL' && gene.mother_expression === null) ||
                (gene.mother_expression === 'NORMAL' && gene.father_expression === null)
            ) {
                // NORMAL X NULL = HET
                results[gene.gene_name].expressions.push('HET');

            } else if (
                (gene.father_expression && isChanceGene({ expression: gene.father_expression }) && gene.mother_expression)
                || (gene.mother_expression && isChanceGene({ expression: gene.mother_expression }) && gene.father_expression)

            ) {
                // CHANCE X Not Null = NORMAL | POSSIBLE_HET
                results[gene.gene_name].expressions.push('NORMAL');
                results[gene.gene_name].expressions.push('POSSIBLE_HET');

            } else if (
                (gene.mother_expression && isChanceGene({ expression: gene.mother_expression }) && gene.father_expression === null)
                || (gene.father_expression && isChanceGene({ expression: gene.father_expression }) && gene.mother_expression === null)
            ) {
                // CHANCE X NULL = POSSIBLE_HET
                results[gene.gene_name].expressions.push('POSSIBLE_HET');
            } else {

                console.log('ERROR', gene, gene.mother_expression, gene.father_expression)
            }
        } else if (geneTypeNormalized === '(Line Bred)') {
            if (gene.mother_expression === 'NORMAL' && gene.father_expression === 'NORMAL') {
                results[gene.gene_name].expressions.push('NORMAL');
            } else if (
                (gene.mother_expression === 'NORMAL' && gene.father_expression === null) ||
                (gene.father_expression === 'NORMAL' && gene.mother_expression === null)) {
                results[gene.gene_name].expressions.push('NORMAL');
                results[gene.gene_name].expressions.push(null);
            } else {
                console.log('ERROR', gene)
            }
        } else if (geneTypeNormalized === '(Unproven)') {
            results[gene.gene_name].expressions.push('NORMAL');
            results[gene.gene_name].expressions.push(null);
        }
    });

    // create a combo that includes one of each gene in results
    // the format should be an array of objects with the gene name and the expression

    let combos: Combos[] = recursiveGeneCombo(Object.values(results), [[] as Combos]);

    return combos;

}

const recursiveGeneCombo = (results: GenePossibility[], currentCombos: Combos[]) => {
    if (results.length === 0) {
        return currentCombos;
    }
    const gene = results[0];
    const newCombos: Combos[] = [];
    currentCombos.forEach((combo) => {
        gene.expressions.forEach((expression) => {
            newCombos.push([...combo, { gene_name: gene.gene_name, gene_type: gene.gene_type, expression }]);
        });
    });
    return recursiveGeneCombo(results.slice(1), newCombos);
}
